import axios from "axios";
import { useCallback } from "react";
import { getAuthToken } from "../context/AuthContext";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/files";

const api = axios.create({
  baseURL: baseUrl,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log("Files API Response:", response);
    return response;
  },
  (error) => {
    console.error("Files API Error:", error.response || error);
    return Promise.reject(error);
  },
);

const useFiles = () => {
  // Get all files for current company
  const getCurrentCompanyFiles = useCallback(async () => {
    try {
      const response = await api.get("/");
      return response.data;
    } catch (error) {
      console.error("Error fetching current company files:", error);
      throw error;
    }
  }, []);

  // Get all files for a specific company (admin only)
  const getFilesByCompanyId = useCallback(async (companyId) => {
    try {
      const response = await api.get(`/company/${companyId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching files for company ${companyId}:`, error);
      throw error;
    }
  }, []);

  // Upload a file for a specific company (admin only)
  const uploadFileForCompany = useCallback(async (companyId, formData) => {
    try {
      const response = await api.post(`/company/${companyId}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading file for company ${companyId}:`, error);
      throw error;
    }
  }, []);

  // Delete a file (admin only)
  const deleteFile = useCallback(async (fileId) => {
    try {
      const response = await api.delete(`/${fileId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting file ${fileId}:`, error);
      throw error;
    }
  }, []);

  // Download a file (now returns signed S3 URL JSON)
  const downloadFile = useCallback(async (fileId) => {
    try {
      const response = await api.get(`/download/${fileId}`);
      const { url, filename } = response.data;
      if (!url) throw new Error("No signed URL returned");
      // Force direct download (no new tab)
      const link = document.createElement("a");
      link.href = url;
      if (filename) link.download = filename; // fallback if disposition missing
      document.body.appendChild(link);
      link.click();
      link.remove();
      return true;
    } catch (error) {
      const serverMessage = error.response?.data?.message;
      console.error(
        `Error downloading file ${fileId}:`,
        serverMessage || error.message,
        error,
      );
      throw new Error(serverMessage || error.message || "Download mislukt");
    }
  }, []);

  // Get preview URL (for inline viewing, no download)
  const getFilePreviewUrl = useCallback(async (fileId) => {
    try {
      const response = await api.get(`/preview/${fileId}`);
      const { url } = response.data;
      if (!url) throw new Error("No preview URL returned");
      return url;
    } catch (error) {
      console.error(`Error getting preview URL for file ${fileId}:`, error);
      throw error;
    }
  }, []);

  // Get all files across all companies (admin only)
  const getAllFiles = useCallback(async () => {
    try {
      const response = await api.get("/all");
      return response.data;
    } catch (error) {
      console.error("Error fetching all files:", error);
      throw error;
    }
  }, []);

  return {
    getCurrentCompanyFiles,
    getFilesByCompanyId,
    uploadFileForCompany,
    deleteFile,
    downloadFile,
    getFilePreviewUrl,
    getAllFiles,
  };
};

export default useFiles;
